import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { useClientAuth } from "@/lib/clientAuth";
import { ArrowUpRight, Loader2, Briefcase, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientSignup() {
    const { client, loading } = useClientAuth();
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        password: "",
        message: "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (client) return <Navigate to="/portal" replace />;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.password) {
            toast.error("Name, email, and password are required");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/clients/signup`, form);
            setDone(true);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Sign up failed";
            toast.error(typeof msg === "string" ? msg : "Sign up failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main
            data-testid="client-signup-page"
            className="min-h-screen bg-ink text-bone flex items-center justify-center px-6 py-16 grain"
        >
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 mb-12">
                    <img src="/logo.png" alt="Aximo Studios" className="h-12 w-auto" />
                </Link>

                <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-ember mb-6">
                    <Briefcase className="w-3.5 h-3.5" />
                    Request portal access
                </div>

                {done ? (
                    <div
                        data-testid="signup-success"
                        className="border border-ember/40 bg-ember/5 p-8"
                    >
                        <CheckCircle2 className="w-8 h-8 text-ember mb-4" />
                        <h2 className="font-display text-3xl tracking-tightest text-bone mb-3">
                            Request received.
                        </h2>
                        <p className="text-sm text-bone-500 leading-relaxed mb-6">
                            We'll review your request and confirm by email — usually
                            inside one business day. Once approved you can sign in
                            with the password you just set.
                        </p>
                        <Link
                            to="/portal/login"
                            data-testid="signup-go-login"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ember hover:text-bone transition-colors"
                        >
                            Go to sign in <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <>
                        <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1] mb-10">
                            Open your
                            <br />
                            <span className="font-serif-i italic text-ember">
                                portal.
                            </span>
                        </h1>
                        <form
                            onSubmit={onSubmit}
                            className="space-y-6 border border-white/10 bg-ink-800/40 p-8"
                            data-testid="client-signup-form"
                        >
                            <Field
                                label="Full name"
                                value={form.name}
                                onChange={set("name")}
                                testid="signup-name"
                                placeholder="Your name"
                            />
                            <Field
                                label="Work email"
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                testid="signup-email"
                                placeholder="you@company.com"
                            />
                            <Field
                                label="Company"
                                value={form.company}
                                onChange={set("company")}
                                testid="signup-company"
                                placeholder="Optional"
                            />
                            <Field
                                label="Choose a password"
                                type="password"
                                value={form.password}
                                onChange={set("password")}
                                testid="signup-password"
                                placeholder="Min. 8 characters"
                            />
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                                    What are you looking to build? (optional)
                                </span>
                                <textarea
                                    value={form.message}
                                    onChange={set("message")}
                                    data-testid="signup-message"
                                    rows={3}
                                    className="w-full bg-transparent border border-white/10 focus:border-ember outline-none p-3 text-bone placeholder:text-bone-500 text-sm transition-colors resize-none"
                                    placeholder="A short note helps us prep before approval"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="client-signup-submit"
                                className="btn-ember w-full inline-flex items-center justify-between h-12 px-5 text-sm font-medium tracking-wide disabled:opacity-60"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <span>Request access</span>
                                )}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </form>
                        <p className="mt-8 text-xs text-bone-500">
                            Accounts are reviewed by our team before activation.
                        </p>
                        <p className="mt-3 text-xs text-bone-500">
                            Already have access?{" "}
                            <Link
                                to="/portal/login"
                                className="text-ember hover:text-bone transition-colors"
                                data-testid="signup-link-login"
                            >
                                Sign in →
                            </Link>
                        </p>
                    </>
                )}
            </div>
        </main>
    );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight transition-colors"
            />
        </label>
    );
}
